/**
 * MailTagPicker — the one tag input the inbox uses (#646).
 *
 * Wraps the shared `TagInput`, so the inbox matches, autocompletes and spells
 * tags exactly the way contacts do — one vocabulary, one set of rules. Used by
 * the rail's "+", the bulk bar and both thread-view entry points.
 *
 * ## Names in, tag_ids out
 *
 * `TagInput` speaks names; the mail API speaks `tag_id`s. Every name the user
 * adds goes through `ensureMailTag` (`POST /api/mail/tags/resolve`, any user),
 * which returns the EXISTING tag when the name is already taken — case
 * -insensitively — and creates it (category `Mail`) only when it is not. So a
 * duplicate cannot be created from here, and a CRM tag is never recategorised.
 *
 * ## Why hand-rolled and not a Radix popover
 *
 * Same reason as `ThreadReaderToolbar`'s menus: a portal escapes the reader's
 * scroll container. The caller positions this panel; it only draws itself.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Tag, X } from 'lucide-react';
import { toast } from 'sonner';

import TagInput from '../TagInput';
import { useAuth } from '../../context/AuthContext';
import { ensureMailTag } from '../../hooks/useMailTags';

export const ORG_WIDE_HINT =
    'Tags are shared across your organisation — a new tag is visible to everyone, including on contacts.';

const fold = (name) => (name || '').trim().toLowerCase();

// The tag endpoints answer 422 with a machine `code`; this is what a user reads.
const ERROR_TEXT = {
    unknown_tag: 'That tag no longer exists — pick it again',
    tags_required: 'Choose a tag to add or remove',
    tag_name_required: 'Type a tag name',
    tag_too_long: 'That tag name is too long',
    too_many_tags: 'Too many tags at once',
    selection_too_broad: 'Too many messages selected — tag fewer at a time',
    tag_too_broad: 'That tag is on too many messages to filter by',
};

/** A tag request's failure, in words. */
export function tagErrorMessage(err, fallback = 'Could not update tags') {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return ERROR_TEXT[detail?.code] || fallback;
}

export default function MailTagPicker({
    /** The target's current tags, `[{ tag_id, name, color }]` — shown as chips. */
    tags = [],
    /** Tags the target MAY carry, offered for removal (bulk: the selection's union). */
    removable = null,
    /** `({ add: [tag_id], remove: [tag_id] }) => Promise|void` */
    onChange,
    onClose,
    heading = 'Tags',
    /** Only create; nothing to apply to (the rail's "+"). */
    createOnly = false,
    /** The control that toggles this picker — a press on it is not an "outside" click. */
    anchorRef = null,
    testId = 'mail-tag-picker',
    className = '',
}) {
    const { getAuthHeader } = useAuth();
    const panelRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);

    // Close on an outside click or Escape — a picker that stays open behind the
    // user's next click is a picker they have to hunt for.
    useEffect(() => {
        // A press on the toggle is left to the toggle — closing here first would
        // let its click reopen the picker it was meant to shut.
        const onDown = (e) => {
            if (anchorRef?.current?.contains(e.target)) return;
            if (panelRef.current && !panelRef.current.contains(e.target)) onClose?.();
        };
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose, anchorRef]);

    const names = tags.map((t) => t.name);

    const handleNames = async (nextNames) => {
        const have = new Set(names.map(fold));
        const want = new Set(nextNames.map(fold));
        const addedNames = nextNames.filter((n) => !have.has(fold(n)));
        const removeIds = tags.filter((t) => !want.has(fold(t.name))).map((t) => t.tag_id);
        if (!addedNames.length && !removeIds.length) return;

        setBusy(true);
        setNotice(null);
        try {
            const addIds = [];
            const reused = [];
            for (const name of addedNames) {
                const res = await ensureMailTag(name, getAuthHeader());
                const tag = res?.tag;
                if (!tag?.tag_id) continue;
                addIds.push(tag.tag_id);
                if (res.created === false && (createOnly || tag.tag !== name.trim())) reused.push(tag.tag);
            }
            if (reused.length) {
                const text = `Using the existing tag ${Array.from(new Set(reused)).join(', ')}`;
                // The rail's "+" closes once it has its tag, so a notice inside the
                // panel would never be read there.
                if (createOnly) toast.info(text);
                else setNotice(text);
            }
            await onChange?.({ add: addIds, remove: removeIds });
        } catch (err) {
            setNotice(tagErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-label={heading}
            data-testid={testId}
            className={`w-[280px] rounded-[10px] border border-[#EDEBE6] bg-white p-3 shadow-lg text-[13px] ${className}`}
        >
            <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-semibold text-stone-800">
                    <Tag className="h-3.5 w-3.5" aria-hidden="true" />
                    {heading}
                </span>
                <button
                    type="button"
                    aria-label="Close"
                    onClick={() => onClose?.()}
                    className="text-stone-400 hover:text-stone-700"
                    data-testid={`${testId}-close`}
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>

            <TagInput
                value={createOnly ? [] : names}
                onChange={handleNames}
                placeholder={createOnly ? 'New tag name…' : 'Add a tag…'}
                createHint={ORG_WIDE_HINT}
            />

            {removable && removable.length > 0 && (
                <div className="mt-3" data-testid={`${testId}-removable`}>
                    <div className="mb-1 text-[11px] text-stone-500">Remove from the selection</div>
                    <div className="flex flex-wrap gap-1.5">
                        {removable.map((t) => (
                            <button
                                key={t.tag_id}
                                type="button"
                                disabled={busy}
                                onClick={() => onChange?.({ add: [], remove: [t.tag_id] })}
                                data-testid={`${testId}-remove-${t.tag_id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs text-stone-700 hover:border-red-300 hover:text-red-600"
                                style={t.color ? { borderLeft: `3px solid ${t.color}` } : undefined}
                            >
                                {t.name}
                                <X className="h-3 w-3" aria-hidden="true" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {(busy || notice) && (
                <div className="mt-2 text-[11px] text-stone-500" data-testid={`${testId}-notice`}>
                    {busy ? 'Saving…' : notice}
                </div>
            )}
        </div>
    );
}

/** A conversation's tags: the distinct tags of its messages, by name. */
export function conversationTagsOf(messages) {
    const seen = new Map();
    (messages || []).forEach((m) => (m?.tags || []).forEach((t) => seen.set(t.tag_id, t)));
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Chips for a message or conversation's tags. Renders nothing when there are none. */
export function MailTagChips({ tags, className = '', testId = 'mail-tag-chips' }) {
    if (!tags || tags.length === 0) return null;
    return (
        <span className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-testid={testId}>
            {tags.map((t) => (
                <span
                    key={t.tag_id}
                    className="inline-flex items-center gap-1 rounded-[5px] border border-stone-200 bg-stone-50 px-1.5 py-[1px] text-[11px] text-stone-700"
                    style={t.color ? { borderLeft: `3px solid ${t.color}` } : undefined}
                >
                    {t.name}
                </span>
            ))}
        </span>
    );
}
